import { useState, useEffect } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useAuth } from '../contexts/AuthContext';

/**
 * useOrgMembership
 *
 * Returns:
 *   org          — the organizations/{orgId} document (or null)
 *   membership   — the organizations/{orgId}/members/{uid} document (or null)
 *   orgIsAdmin   — true if membership.role === 'admin' and status === 'active'
 *   orgIsOwner   — true if org.ownerUid === currentUser.uid
 *   loading      — true until both org and membership have resolved
 */
export function useOrgMembership() {
  const { currentUser, userDoc } = useAuth();
  const [org, setOrg]               = useState(null);
  const [membership, setMembership] = useState(null);
  const [loading, setLoading]       = useState(true);

  useEffect(() => {
    if (!currentUser || !userDoc) {
      setOrg(null);
      setMembership(null);
      setLoading(false);
      return;
    }

    const orgId = userDoc.orgId;
    if (!orgId) {
      setOrg(null);
      setMembership(null);
      setLoading(false);
      return;
    }

    let orgResolved        = false;
    let membershipResolved = false;

    function checkDone() {
      if (orgResolved && membershipResolved) setLoading(false);
    }

    const unsubOrg = onSnapshot(doc(db, 'organizations', orgId), (snap) => {
      setOrg(snap.exists() ? { id: snap.id, ...snap.data() } : null);
      orgResolved = true;
      checkDone();
    });

    const unsubMember = onSnapshot(
      doc(db, 'organizations', orgId, 'members', currentUser.uid),
      (snap) => {
        setMembership(snap.exists() ? { id: snap.id, ...snap.data() } : null);
        membershipResolved = true;
        checkDone();
      }
    );

    return () => {
      unsubOrg();
      unsubMember();
    };
  }, [currentUser, userDoc?.orgId]);

  const orgIsAdmin = membership?.role === 'admin' && membership?.status === 'active';
  const orgIsOwner = org && currentUser ? org.ownerUid === currentUser.uid : false;

  return { org, membership, orgIsAdmin, orgIsOwner, loading };
}
